// Company-level storytelling for the homepage. Nothing here describes a single product:
// the pictures are abstract (scattered work → understood → coordinated → clear outcome)
// and every line of copy also exists as ordinary text, so the story reads without motion.
import { useEffect, useRef, useState } from "react";
import { AnimatePresence, LayoutGroup, motion, useInView, useReducedMotion, useScroll, useTransform, type MotionValue } from "framer-motion";
import { ArrowRight, Check, Sparkles } from "lucide-react";

const isPrerender = () => typeof navigator !== "undefined" && /HeadlessChrome/.test(navigator.userAgent);
const EASE = [0.22, 1, 0.36, 1] as const;

const KINDS = [
  { label: "Request", c: "hsl(216 84% 56%)" },
  { label: "Deadline", c: "hsl(0 72% 58%)" },
  { label: "Question", c: "hsl(38 95% 52%)" },
  { label: "Approval", c: "hsl(150 55% 42%)" },
  { label: "File", c: "hsl(262 55% 60%)" },
  { label: "Follow-up", c: "hsl(216 84% 56%)" },
  { label: "Note", c: "hsl(38 95% 52%)" },
  { label: "Invoice", c: "hsl(150 55% 42%)" },
  { label: "Meeting", c: "hsl(262 55% 60%)" },
  { label: "Feedback", c: "hsl(0 72% 58%)" },
  { label: "Task", c: "hsl(216 84% 56%)" },
  { label: "Update", c: "hsl(150 55% 42%)" },
];
// hand-placed so the "before" state looks like a desk, not a grid
const SCATTER = [
  [9, 22, -9], [27, 66, 7], [44, 14, 5], [62, 74, -6], [80, 20, 10], [91, 62, -8],
  [16, 84, 4], [36, 40, -12], [55, 48, 8], [72, 44, -4], [88, 86, 6], [6, 52, 11],
];

/* ── Hero: scattered work settles into order, then loosens again ──────────── */
export const HeroField = () => {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { margin: "-40px" });
  const reduce = useReducedMotion();
  const still = reduce || isPrerender();
  const [ordered, setOrdered] = useState(still);

  useEffect(() => {
    if (still || !inView) return;
    const t = setInterval(() => setOrdered((o) => !o), 3400);
    const first = setTimeout(() => setOrdered(true), 900);
    return () => { clearInterval(t); clearTimeout(first); };
  }, [still, inView]);

  const cols = ["Now", "Next", "Done"];
  return (
    <div ref={ref} aria-hidden className="relative mx-auto aspect-[4/3] w-full max-w-5xl sm:aspect-[16/7]">
      <div className="absolute inset-0 rounded-[28px] bg-[hsl(40_9%_96%)]" />
      {cols.map((c, i) => (
        <motion.span
          key={c}
          className="absolute top-[7%] text-[12px] font-semibold uppercase tracking-[0.1em] text-muted-foreground sm:text-[13px]"
          style={{ left: `${22 + i * 28}%` }}
          animate={{ opacity: ordered ? 1 : 0, y: ordered ? 0 : 6, x: "-50%" }}
          transition={{ duration: 0.5, delay: ordered ? 0.35 : 0 }}
        >
          {c}
        </motion.span>
      ))}
      {KINDS.map((k, i) => {
        const [sx, sy, sr] = SCATTER[i];
        const col = i % 3, row = Math.floor(i / 3);
        return (
          <motion.span
            key={k.label}
            className="absolute"
            initial={false}
            animate={{ left: `${ordered ? 22 + col * 28 : sx}%`, top: `${ordered ? 24 + row * 18 : sy}%`, rotate: ordered ? 0 : sr }}
            transition={{ duration: 1.1, delay: i * 0.035, ease: EASE }}
          >
            <span className={`flex -translate-x-1/2 -translate-y-1/2 items-center gap-2 whitespace-nowrap rounded-xl border border-border bg-background px-3 py-2 text-[12px] font-medium text-foreground shadow-[0_10px_30px_-18px_hsl(0_0%_0%/0.35)] sm:px-4 sm:py-2.5 sm:text-[14px] ${i > 8 ? "hidden sm:flex" : ""}`}>
              <span className="h-2 w-2 rounded-full" style={{ background: k.c }} />
              {k.label}
              <motion.span animate={{ opacity: ordered && col === 2 ? 1 : 0, width: ordered && col === 2 ? 14 : 0 }} className="overflow-hidden">
                <Check className="h-3.5 w-3.5 text-[hsl(150_55%_38%)]" strokeWidth={3} />
              </motion.span>
            </span>
          </motion.span>
        );
      })}
      <motion.span
        className="absolute left-1/2 top-1/2 -ml-6 -mt-6 flex h-12 w-12 items-center justify-center rounded-2xl bg-foreground text-background"
        animate={{ scale: ordered ? 0 : 1, opacity: ordered ? 0 : 1 }}
        transition={{ duration: 0.4 }}
      >
        <Sparkles className="h-5 w-5" />
      </motion.span>
    </div>
  );
};

/* ── Scroll story: one picture that changes as the page moves ─────────────── */
const CHAPTERS = [
  { k: "01", title: "Work arrives scattered.", body: "Requests, files, questions and deadlines land in different places, in different shapes. Most of the day goes to finding them, not doing them." },
  { k: "02", title: "AI understands what each piece is.", body: "Our products read the work the way a capable colleague would: what is being asked, how urgent it is, and what it depends on." },
  { k: "03", title: "Agents coordinate the next step.", body: "Related pieces are grouped, routine steps are carried out, and anything that needs judgement is prepared for a person to decide." },
  { k: "04", title: "You get a clear outcome.", body: "What is left is short and obvious: what was done, what is waiting on you, and nothing else." },
];
// The picture: a messy pile of work is read, sorted into who-does-what, and reduced to
// the two things that actually need a person.
type Fate = "handled" | "you" | "filed";
const DOCS: { title: string; tag: string; c: string; fate: Fate }[] = [
  { title: "Supplier renewal", tag: "Routine", c: "hsl(150 55% 42%)", fate: "handled" },
  { title: "Where is my order?", tag: "Routine", c: "hsl(150 55% 42%)", fate: "handled" },
  { title: "Meeting on Thursday?", tag: "Schedule", c: "hsl(262 55% 62%)", fate: "handled" },
  { title: "Weekly feedback", tag: "Summary", c: "hsl(216 84% 58%)", fate: "handled" },
  { title: "Invoice #2041", tag: "Routine", c: "hsl(150 55% 42%)", fate: "handled" },
  { title: "Budget sign-off", tag: "Decision", c: "hsl(38 95% 54%)", fate: "you" },
  { title: "Upset customer", tag: "Urgent", c: "hsl(0 72% 60%)", fate: "you" },
  { title: "Newsletter", tag: "FYI", c: "hsl(0 0% 60%)", fate: "filed" },
];
const PILE = [[38, 40, -14], [56, 34, 9], [47, 56, -5], [63, 60, 13], [32, 63, 7], [52, 45, -20], [69, 41, -8], [42, 72, 16]];
const STOPS = [0, 0.2, 0.3, 0.46, 0.58, 0.74, 0.86, 1];

const Doc = ({ i, p }: { i: number; p: MotionValue<number> }) => {
  const d = DOCS[i];
  const [px, py, pr] = PILE[i];
  const gx = 12.5 + (i % 4) * 25, gy = 30 + Math.floor(i / 4) * 40;
  const k = DOCS.slice(0, i).filter((x) => x.fate === d.fate).length;
  const [sx, sy] = d.fate === "handled" ? [18, 28 + k * 11] : d.fate === "you" ? [50, 36 + k * 30] : [82, 40];
  const [fx, fy] = d.fate === "you" ? [29 + k * 42, 54] : [sx, sy];
  const left = useTransform(p, STOPS, [px, px, gx, gx, sx, sx, fx, fx].map((v) => `${v}%`));
  const top = useTransform(p, STOPS, [py, py, gy, gy, sy, sy, fy, fy].map((v) => `${v}%`));
  const rotate = useTransform(p, [0, 0.2, 0.3], [pr, pr, 0]);
  const scale = useTransform(p, [0.74, 0.86], d.fate === "you" ? [1, 1.28] : [1, 0.7]);
  const opacity = useTransform(p, [0.74, 0.84], d.fate === "you" ? [1, 1] : [1, 0]);
  const tagOpacity = useTransform(p, [0.3 + (i % 4) * 0.03, 0.34 + (i % 4) * 0.03], [0, 1]);
  const doneOpacity = useTransform(p, [0.6, 0.66], [0, d.fate === "handled" ? 1 : 0]);
  const actOpacity = useTransform(p, [0.86, 0.94], [0, d.fate === "you" ? 1 : 0]);
  return (
    <motion.div className="absolute" style={{ left, top, rotate, scale, opacity, zIndex: d.fate === "you" ? 30 : 10 + i }}>
      <div className="w-[112px] -translate-x-1/2 -translate-y-1/2 rounded-xl border border-white/15 bg-[hsl(0_0%_13%)] p-3 shadow-[0_18px_40px_-18px_#000] sm:w-[128px] sm:p-3">
        <div className="flex h-4 items-center justify-between">
          <motion.span style={{ opacity: tagOpacity, color: d.c, borderColor: d.c }} className="rounded border px-1.5 text-[9px] font-semibold uppercase tracking-[0.06em] sm:text-[10px]">{d.tag}</motion.span>
          <motion.span style={{ opacity: doneOpacity }} className="flex h-4 w-4 items-center justify-center rounded-full bg-[hsl(150_55%_42%)] text-white"><Check className="h-2.5 w-2.5" strokeWidth={4} /></motion.span>
        </div>
        <p className="mt-2 truncate text-[11px] font-semibold text-white sm:text-[13px]">{d.title}</p>
        <span className="mt-2 block h-1.5 w-[85%] rounded-full bg-white/15" />
        <span className="mt-1.5 block h-1.5 w-[60%] rounded-full bg-white/15" />
        <motion.span style={{ opacity: actOpacity }} className="mt-2.5 block rounded-md bg-white py-1 text-center text-[10px] font-semibold text-black sm:text-[11px]">
          {i === 5 ? "Approve" : "Reply ready"}
        </motion.span>
      </div>
    </motion.div>
  );
};

const StoryVisual = ({ p }: { p: MotionValue<number> }) => {
  const scanX = useTransform(p, [0.28, 0.46], ["2%", "98%"]);
  const scanOpacity = useTransform(p, [0.28, 0.31, 0.43, 0.46], [0, 1, 1, 0]);
  const lanesOpacity = useTransform(p, [0.56, 0.62, 0.74, 0.8], [0, 1, 1, 0]);
  const tallyOpacity = useTransform(p, [0.86, 0.94], [0, 1]);
  return (
    <div aria-hidden className="relative h-[42svh] w-full max-w-[640px] lg:aspect-[5/4] lg:h-auto">
      <motion.span style={{ left: scanX, opacity: scanOpacity }} className="absolute inset-y-[8%] z-30 w-px bg-[hsl(216_84%_62%)] shadow-[0_0_24px_4px_hsl(216_84%_56%/0.6)]" />
      {[["Handled by agents", 18], ["Needs you", 50], ["Filed", 82]].map(([l, x]) => (
        <motion.span key={l} style={{ opacity: lanesOpacity, left: `${x}%`, x: "-50%" }} className="absolute top-[6%] whitespace-nowrap font-mono text-[10px] uppercase tracking-[0.1em] text-white/60 sm:text-[12px]">
          {l}
        </motion.span>
      ))}
      <motion.p style={{ opacity: tallyOpacity, x: "-50%" }} className="absolute left-1/2 top-[8%] whitespace-nowrap text-center text-[15px] font-medium text-white/80 sm:text-[18px]">
        <span className="text-white/40 line-through">8 things</span> <span className="ml-2 font-semibold text-white">2 need you</span>
      </motion.p>
      {DOCS.map((_, i) => <Doc key={i} i={i} p={p} />)}
    </div>
  );
};

const Chapter = ({ i, p }: { i: number; p: MotionValue<number> }) => {
  const a = i / 4, b = (i + 1) / 4;
  const opacity = useTransform(p, i === 0 ? [0, b - 0.06, b] : i === 3 ? [a, a + 0.06, 1] : [a, a + 0.06, b - 0.06, b], i === 0 ? [1, 1, 0] : i === 3 ? [0, 1, 1] : [0, 1, 1, 0]);
  const y = useTransform(p, [a, a + 0.06], [i === 0 ? 0 : 24, 0]);
  const c = CHAPTERS[i];
  return (
    <motion.div style={{ opacity, y }} className="absolute inset-x-0 top-0">
      <p className="font-mono text-[13px] text-white/50">{c.k} / 04</p>
      <h3 className="mt-2 text-[1.6rem] font-semibold leading-[1.1] tracking-[-0.03em] text-white sm:text-[2.8rem]">{c.title}</h3>
      <p className="mt-3 max-w-md text-[15px] leading-relaxed text-white/75 sm:mt-4 sm:text-[19px]">{c.body}</p>
    </motion.div>
  );
};

const Progress = ({ p }: { p: MotionValue<number> }) => {
  const width = useTransform(p, [0, 1], ["0%", "100%"]);
  return (
    <div className="h-1 w-full overflow-hidden rounded-full bg-white/15">
      <motion.div style={{ width }} className="h-full rounded-full bg-[hsl(216_84%_56%)]" />
    </div>
  );
};

// Without motion (or in the static HTML) the same story is a plain four-step list.
const StaticStory = () => (
      <section aria-labelledby="story-h" className="bg-[hsl(0_0%_7%)] py-24 text-white sm:py-32">
        <div className="cm-container">
          <h2 id="story-h" className="max-w-3xl text-[2.2rem] font-semibold leading-[1.08] tracking-[-0.03em] sm:text-[3.2rem]">From scattered work to a clear outcome.</h2>
          <ol className="mt-14 grid gap-10 sm:grid-cols-2 lg:grid-cols-4">
            {CHAPTERS.map((c) => (
              <li key={c.k}>
                <p className="font-mono text-[13px] text-white/50">{c.k}</p>
                <h3 className="mt-2 text-xl font-semibold tracking-[-0.02em]">{c.title}</h3>
                <p className="mt-2 text-[15px] leading-relaxed text-white/75">{c.body}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>
);

const PinnedStory = () => {
  const ref = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end end"] });
  return (
    <section ref={ref} aria-labelledby="story-h" className="relative h-[420vh] bg-[hsl(0_0%_7%)]">
      <div className="sticky top-0 flex h-[100svh] flex-col overflow-hidden">
        <div className="cm-container flex flex-1 flex-col justify-center gap-2 pb-16 pt-16 lg:grid lg:grid-cols-[1fr_1.15fr] lg:items-center lg:gap-12 lg:pt-20">
          <div>
            <h2 id="story-h" className="text-[13px] font-semibold uppercase tracking-[0.1em] text-white/60">From scattered work to a clear outcome</h2>
            <div className="relative mt-4 h-[200px] sm:mt-6 sm:h-[300px]">
              {CHAPTERS.map((_, i) => <Chapter key={i} i={i} p={scrollYProgress} />)}
            </div>
          </div>
          <div className="flex justify-center"><StoryVisual p={scrollYProgress} /></div>
        </div>
        <div className="cm-container absolute inset-x-0 bottom-0 flex items-center gap-4 pb-5 sm:pb-8">
          <div className="flex-1"><Progress p={scrollYProgress} /></div>
          <button
            type="button"
            onClick={() => {
              const n = ref.current;
              if (n) window.scrollTo({ top: n.getBoundingClientRect().bottom + window.scrollY - 64, behavior: "smooth" });
            }}
            className="rounded-full border border-white/60 px-6 py-1.5 text-[13px] font-medium text-white transition-colors hover:bg-white hover:text-black"
          >
            Skip
          </button>
        </div>
      </div>
    </section>
  );
};

/* ── Problem areas, each with a labelled illustrative scenario ────────────── */
const AREAS = [
  {
    key: "channels", tab: "Too many channels", color: "hsl(216 84% 52%)",
    title: "Attention is split across more places than anyone can watch.",
    steps: ["A small team receives requests in six different places.", "An agent reads each one and works out what is actually being asked.", "Routine requests are answered; the rest are queued by urgency.", "People open one short list instead of six apps."],
  },
  {
    key: "coordination", tab: "Endless coordination", color: "hsl(32 90% 48%)",
    title: "Simple things take ten messages to arrange.",
    steps: ["Three people need to agree a time, a place and an agenda.", "An agent checks what each person has already said and what is free.", "It proposes one option and handles the back-and-forth.", "Everyone gets a confirmed plan. Nobody chased anybody."],
  },
  {
    key: "knowledge", tab: "Buried knowledge", color: "hsl(150 55% 36%)",
    title: "The answer exists. Nobody can find it.",
    steps: ["A new colleague asks a question that was answered last spring.", "An agent searches past work and finds the decision and who made it.", "It replies with the answer and links to the source.", "The question is closed in a minute, not a meeting."],
  },
  {
    key: "decisions", tab: "Stalled decisions", color: "hsl(262 55% 55%)",
    title: "Work waits because one person has not seen it yet.",
    steps: ["An approval sits unread while a deadline gets closer.", "An agent notices the risk and gathers what the approver needs.", "It puts a one-line decision in front of the right person.", "Approved in seconds, with the full context one click away."],
  },
];
const STEP_LABELS = ["Problem", "AI understands", "Automation", "Result"];

export const ProblemAreas = () => {
  const [key, setKey] = useState(AREAS[0].key);
  const [step, setStep] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { margin: "-100px" });
  const reduce = useReducedMotion();
  const still = reduce || isPrerender();
  const a = AREAS.find((x) => x.key === key)!;

  useEffect(() => setStep(0), [key]);
  useEffect(() => {
    if (still || !inView) return;
    const t = setInterval(() => setStep((s) => (s + 1) % 4), 2600);
    return () => clearInterval(t);
  }, [still, inView, key]);

  return (
    <div ref={ref}>
      <LayoutGroup id="areas">
        <div className="-mx-4 overflow-x-auto px-4 cm-noscroll sm:mx-0 sm:px-0">
          <div role="tablist" aria-label="Problem areas" className="mx-auto flex w-max gap-1 rounded-full bg-[hsl(40_9%_95%)] p-1.5">
            {AREAS.map((t) => (
              <button key={t.key} role="tab" type="button" aria-selected={t.key === key} onClick={() => setKey(t.key)}
                className={`relative rounded-full px-5 py-2.5 text-[15px] font-medium transition-colors duration-200 sm:px-6 ${t.key === key ? "text-background" : "text-foreground hover:bg-[hsl(40_9%_90%)]"}`}>
                {t.key === key && <motion.span layoutId="area-pill" className="absolute inset-0 rounded-full bg-foreground" transition={{ type: "spring", stiffness: 400, damping: 34 }} />}
                <span className="relative whitespace-nowrap">{t.tab}</span>
              </button>
            ))}
          </div>
        </div>
      </LayoutGroup>

      <AnimatePresence mode="wait" initial={false}>
        <motion.div key={a.key} role="tabpanel" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.28 }} className="mt-14">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <h3 className="max-w-3xl text-[2rem] font-semibold leading-[1.1] tracking-[-0.03em] sm:text-[2.7rem]">{a.title}</h3>
            <span className="w-fit shrink-0 rounded-full border border-border px-3 py-1 text-[12px] font-medium uppercase tracking-[0.08em] text-muted-foreground">Illustrative scenario</span>
          </div>
          <ol className="mt-10 grid gap-4 md:grid-cols-4">
            {a.steps.map((s, i) => {
              const on = still || i <= step;
              return (
                <motion.li key={s} animate={{ opacity: on ? 1 : 0.4, y: on ? 0 : 6 }} transition={{ duration: 0.45, ease: EASE }}
                  onMouseEnter={() => setStep(i)}
                  className="relative flex min-h-[210px] flex-col rounded-3xl p-6 transition-colors duration-500"
                  style={{ background: i === 3 && on ? a.color : "hsl(40 9% 96%)", color: i === 3 && on ? "#fff" : undefined }}>
                  <span className="flex items-center gap-2 text-[12px] font-semibold uppercase tracking-[0.08em] opacity-70">
                    <span className="flex h-6 w-6 items-center justify-center rounded-full text-[11px] text-white" style={{ background: i === 3 && on ? "hsl(0 0% 100% / 0.25)" : a.color }}>{i + 1}</span>
                    {STEP_LABELS[i]}
                  </span>
                  <span className="mt-auto pt-8 text-[17px] font-medium leading-snug tracking-[-0.01em]">{s}</span>
                  {i < 3 && <ArrowRight className="absolute -right-3.5 top-1/2 z-10 hidden h-5 w-5 -translate-y-1/2 text-[hsl(0_0%_70%)] md:block" />}
                </motion.li>
              );
            })}
          </ol>
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export const ScrollStory = () => {
  const reduce = useReducedMotion();
  return reduce || isPrerender() ? <StaticStory /> : <PinnedStory />;
};
